import React, { useState } from "react";
import { motion } from "framer-motion";
import { Send, Loader2, Phone, MapPin, Mail } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Thanks! We'll be in touch.");
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 relative border-t border-white/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid gap-12 lg:grid-cols-2">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-neon-cyan mb-3">// let's talk</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter">
            Have an idea? <br />
            <span className="text-neon-cyan">Let's ship it.</span>
          </h2>
          <p className="mt-5 text-white/55 max-w-md leading-relaxed">
            Tell us what you want to build. We'll reply within 24 hours with a plan, price and timeline.
          </p>

          <div className="mt-10 space-y-4 text-sm">
            <div className="flex items-center gap-3 text-white/70">
              <span className="w-9 h-9 rounded-lg border border-neon-cyan/30 flex items-center justify-center">
                <Phone size={16} className="text-neon-cyan" />
              </span>
              <a href="tel:+19047891050" data-testid="contact-phone" className="hover:text-neon-cyan transition-colors">+1 904-789-1050</a>
            </div>
            <div className="flex items-center gap-3 text-white/70">
              <span className="w-9 h-9 rounded-lg border border-neon-cyan/30 flex items-center justify-center">
                <Mail size={16} className="text-neon-cyan" />
              </span>
              <span>Abhilash Reddy</span>
            </div>
            <div className="flex items-center gap-3 text-white/70">
              <span className="w-9 h-9 rounded-lg border border-neon-cyan/30 flex items-center justify-center">
                <MapPin size={16} className="text-neon-cyan" />
              </span>
              <span>Jacksonville, FL · Hyderabad, India</span>
            </div>
          </div>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          onSubmit={submit}
          data-testid="contact-form"
          className="rounded-2xl border border-neon-cyan/15 bg-surface p-6 sm:p-8 space-y-4"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-white/60">Name *</Label>
              <Input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-void border-white/15 mt-1" />
            </div>
            <div>
              <Label className="text-xs text-white/60">Email *</Label>
              <Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-void border-white/15 mt-1" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-white/60">Phone</Label>
              <Input data-testid="contact-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-void border-white/15 mt-1" />
            </div>
            <div>
              <Label className="text-xs text-white/60">Company</Label>
              <Input data-testid="contact-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="bg-void border-white/15 mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-white/60">Message *</Label>
            <Textarea data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-void border-white/15 mt-1" rows={4} placeholder="What do you want to build?" />
          </div>
          <button
            type="submit"
            data-testid="contact-submit"
            disabled={submitting}
            className="neon-btn w-full bg-neon-cyan text-black font-semibold py-3.5 rounded-xl inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {submitting ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
            Send message
          </button>
        </motion.form>
      </div>
    </section>
  );
}
